import { useEffect, useRef, useState } from "react";

import { ArrowRight, Sparkles, Calculator } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useT } from "@/i18n/LocaleProvider";
import heroAvif from "@/assets/kentro-fluid-hero-4x3.avif.asset.json";
import heroAvif640 from "@/assets/kentro-fluid-hero-640.avif.asset.json";
import heroAvif960 from "@/assets/kentro-fluid-hero-960.avif.asset.json";
import heroAvif1280 from "@/assets/kentro-fluid-hero-1280.avif.asset.json";
import heroWebp from "@/assets/kentro-fluid-hero-4x3.webp.asset.json";
import heroWebp640 from "@/assets/kentro-fluid-hero-640.webp.asset.json";
import heroWebp960 from "@/assets/kentro-fluid-hero-960.webp.asset.json";
import heroWebp1280 from "@/assets/kentro-fluid-hero-1280.webp.asset.json";


export function HeroV2() {
  const t = useT();
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [iframeError, setIframeError] = useState(false);
  // Defer iframe mount so the LCP (text + fallback image) paints first.
  const [shouldMountIframe, setShouldMountIframe] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    let cancelled = false;
    const mount = () => {
      if (!cancelled) setShouldMountIframe(true);
    };

    // Wait for the browser to be idle (post-LCP) before loading the iframe.
    const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback;
    let idleId: number | undefined;
    let timeoutId: number | undefined;
    if (typeof ric === "function") {
      idleId = ric(mount, { timeout: 1500 });
    } else {
      timeoutId = window.setTimeout(mount, 800);
    }

    // Safety net: ensure placeholder doesn't linger on slow networks.
    const safety = window.setTimeout(() => setIframeLoaded(true), 8000);

    return () => {
      cancelled = true;
      if (idleId !== undefined) {
        const cic = (window as unknown as { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback;
        cic?.(idleId);
      }
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
      window.clearTimeout(safety);
    };
  }, []);


  return (
    <section className="gradient-hero relative overflow-hidden pt-10 pb-12 md:pt-14 md:pb-16">
      <div className="circuit-pattern absolute inset-0 opacity-30" />
      <div className="absolute -top-40 left-1/3 size-[640px] -translate-x-1/2 rounded-full bg-primary/25 blur-3xl" />
      <div className="absolute -bottom-40 right-0 size-[420px] rounded-full bg-[var(--mint)]/15 blur-3xl" />

      <div className="relative mx-auto grid w-full max-w-[90rem] grid-cols-1 items-center gap-8 px-4 sm:px-5 lg:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)] lg:gap-5 lg:px-6 xl:px-8">
        <div className="relative z-20 min-w-0 lg:max-w-[540px] lg:justify-self-start animate-in fade-in slide-in-from-bottom-4 duration-500">


          <span className="glass-card inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-primary-glow">
            <Sparkles className="size-3.5" /> {t.hero.badge}
          </span>
          <h1
            className="mt-4 text-3xl font-bold tracking-tight md:text-4xl lg:text-[2.5rem] lg:leading-[1.1]"
            style={{ fontFamily: "var(--font-display)" }}
          >
            {t.hero.titleA}
            <span className="gradient-text">{t.hero.titleHighlight}</span>
          </h1>
          <p
            className="mt-5 max-w-lg text-lg text-muted-foreground"
            dangerouslySetInnerHTML={{ __html: t.hero.descriptionHtml }}
          />

          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg" className="gradient-primary text-white shadow-glow hover:opacity-95">
              <a href="#contato">
                {t.hero.ctaPrimary} <ArrowRight />
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white/15 bg-white/5 text-foreground hover:bg-white/10">
              <a href="#calculadora">
                <Calculator className="mr-1" /> {t.hero.ctaSecondary}
              </a>
            </Button>
          </div>

          <p className="mt-8 text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">{t.hero.social1}</span> ·{" "}
            <span className="font-semibold text-foreground">{t.hero.social2}</span> ·{" "}
            <span className="font-semibold text-foreground">{t.hero.social3}</span>
          </p>
        </div>

        <div
          className="relative z-10 min-w-0 lg:-ml-28 xl:-ml-44 lg:w-[calc(100%+7rem)] xl:w-[calc(100%+11rem)]"
        >
          <div ref={containerRef} className="glass-card relative w-full overflow-hidden rounded-3xl shadow-elegant aspect-[3/2]">
            {/* Fallback image / placeholder (acts as LCP candidate) */}
            <picture
              className={`absolute inset-0 z-0 transition-opacity duration-700 ${iframeLoaded && !iframeError ? "opacity-0" : "opacity-100"}`}
              aria-hidden={iframeLoaded && !iframeError ? "true" : "false"}
            >
              <source
                type="image/avif"
                srcSet={`${heroAvif640.url} 640w, ${heroAvif960.url} 960w, ${heroAvif1280.url} 1280w`}
                sizes="(min-width: 1024px) 55vw, 100vw"
              />
              <source
                type="image/webp"
                srcSet={`${heroWebp640.url} 640w, ${heroWebp960.url} 960w, ${heroWebp1280.url} 1280w`}
                sizes="(min-width: 1024px) 55vw, 100vw"
              />
              <img
                src={heroWebp960.url}
                srcSet={`${heroWebp640.url} 640w, ${heroWebp960.url} 960w, ${heroWebp1280.url} 1280w`}
                sizes="(min-width: 1024px) 55vw, 100vw"
                alt={t.hero.mockupTitle}
                width={1200}
                height={800}
                className="block h-full w-full object-cover"
                loading="eager"
                decoding="async"
                fetchPriority="high"
              />
            </picture>

            {/* Loading shimmer */}
            {!iframeLoaded && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-sm">
                <div className="h-full w-full animate-pulse bg-gradient-to-r from-transparent via-white/10 to-transparent" />
              </div>
            )}

            {shouldMountIframe && (
              <iframe
                src="/hero/kentro-hero-cena.html"
                title={t.hero.mockupTitle}
                loading="lazy"
                referrerPolicy="no-referrer"
                sandbox="allow-scripts allow-same-origin"
                className="absolute inset-0 z-20 block h-full w-full border-0"
                aria-label={t.hero.mockupTitle}
                allow="fullscreen"
                onLoad={() => setIframeLoaded(true)}
                onError={() => {
                  setIframeError(true);
                  setIframeLoaded(true);
                }}
              >
                <p className="p-6 text-sm text-muted-foreground">
                  {t.hero.mockupTitle}
                </p>
              </iframe>
            )}
          </div>
        </div>


      </div>
    </section>
  );
}
